import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { X, ChevronLeft, ChevronRight, Phone } from 'lucide-react';
import axios from 'axios';
import { API } from '../lib/api';



const Gallery = () => {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API}/gallery`);
        setGallery(response.data);
      } catch (error) {
        console.error('Error fetching gallery:', error);
      }
      setLoading(false);
    };
    fetchGallery();
  }, []);

  const filters = [
    { id: 'all', label: 'All Projects' },
    { id: 'residential', label: 'Residential' },
    { id: 'exterior', label: 'Exterior' },
  ];

  const filteredGallery = activeFilter === 'all' 
    ? gallery 
    : gallery.filter(item => item.category === activeFilter);

  const groupedGallery = filteredGallery.reduce((groups, item) => {
    const project = item.project || 'More Projects';
    const existing = groups.find(g => g.project === project);
    if (existing) {
      existing.items.push(item);
    } else {
      groups.push({ project, location: item.location, items: [item] });
    }
    return groups;
  }, []);

  const openLightbox = (item, index) => {
    setSelectedImage(item);
    setSelectedIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const navigateImage = (direction) => {
    const newIndex = direction === 'next' 
      ? (selectedIndex + 1) % filteredGallery.length
      : (selectedIndex - 1 + filteredGallery.length) % filteredGallery.length;
    setSelectedIndex(newIndex);
    setSelectedImage(filteredGallery[newIndex]);
  };

  return (
    <div data-testid="gallery-page">
      <Helmet>
        <title>Project Gallery | Construction Work Portfolio | S&S Group Services Vernon BC</title>
        <meta name="description" content="View our completed construction projects across BC - residential builds, commercial spaces, townhomes, apartment buildings. See the quality of S&S Group Services work." />
        <meta name="keywords" content="construction projects BC, townhome builder BC, apartment construction Vernon, construction portfolio, Campfire townhomes, Edgewater building" />
        <link rel="canonical" href="https://www.ssgroupservices.ca/gallery" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.ssgroupservices.ca/gallery" />
        <meta property="og:title" content="Project Gallery | S&S Group Services Vernon BC" />
        <meta property="og:description" content="Real construction projects across BC - townhomes, apartments, residential & commercial builds." />
        <meta property="og:image" content="https://customer-assets-v7afamib.emergentagent.net/job_05714f6e-3774-4985-88d6-8908b452b8f7/artifacts/d7qp46h7_IMG_8178.webp" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Project Gallery | S&S Group Services" />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.ssgroupservices.ca/" },
          { "@type": "ListItem", "position": 2, "name": "Gallery", "item": "https://www.ssgroupservices.ca/gallery" }
        ]
      }) }} />
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900" data-testid="gallery-hero">
        <div className="absolute inset-0 grid-pattern" />
        <div className="container relative z-10">
          <div className="max-w-3xl">
            <span className="caption text-blue-400">Our Portfolio</span>
            <h1 className="heading-xl text-white mt-3 mb-6">Project Gallery</h1>
            <p className="text-slate-300 text-lg">
              Browse our collection of completed commercial and residential projects across British Columbia. Each project showcases our commitment to quality craftsmanship.
            </p>
          </div>
        </div>
      </section>

      {/* Filter & Gallery */}
      <section className="section-padding bg-white" data-testid="gallery-grid-section">
        <div className="container">
          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-6 py-3 font-semibold uppercase tracking-wider text-sm transition-colors ${
                  activeFilter === filter.id
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                style={{ fontFamily: 'Oswald, sans-serif' }}
                data-testid={`filter-${filter.id}`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {/* Gallery Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : (
            <div className="space-y-16">
              {groupedGallery.map((group) => (
                <div key={group.project} data-testid={`project-group-${group.project.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="flex items-end justify-between border-b-2 border-slate-900 pb-4 mb-8">
                    <div>
                      <span className="caption text-blue-600">Project</span>
                      <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Oswald, sans-serif' }}>
                        {group.project}
                      </h2>
                    </div>
                    {group.location && (
                      <p className="text-slate-500 text-sm font-semibold uppercase tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>
                        {group.location}
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {group.items.map((item, index) => (
                      <div
                        key={item.id}
                        className={`gallery-item cursor-pointer ${
                          index === 0 ? 'md:col-span-2 md:row-span-2' : ''
                        }`}
                        onClick={() => openLightbox(item, filteredGallery.indexOf(item))}
                        data-testid={`gallery-item-${item.id}`}
                      >
                        <img
                          src={item.image}
                          alt={`${item.title} - ${item.project} construction project by S&S Group Services in BC, Canada`}
                          loading="lazy"
                          className={index === 0 ? 'h-full min-h-[400px]' : 'h-48 md:h-56'}
                        />
                        <div className="absolute inset-0 flex items-end p-4 z-10 opacity-0 hover:opacity-100 transition-opacity">
                          <div className="text-white">
                            <p className="font-semibold" style={{ fontFamily: 'Oswald, sans-serif' }}>{item.title}</p>
                            <p className="text-sm text-slate-300 capitalize">{item.category}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {filteredGallery.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-slate-500">No projects found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <Dialog open={!!selectedImage} onOpenChange={closeLightbox}>
        <DialogContent className="max-w-5xl p-0 bg-black border-none">
          <DialogTitle className="sr-only">{selectedImage ? selectedImage.title : 'Project photo'}</DialogTitle>
          <div className="relative">
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-20 w-10 h-10 bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              data-testid="lightbox-close"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            
            {filteredGallery.length > 1 && (
              <>
                <button
                  onClick={() => navigateImage('prev')}
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                  data-testid="lightbox-prev"
                >
                  <ChevronLeft className="w-8 h-8 text-white" />
                </button>
                <button
                  onClick={() => navigateImage('next')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                  data-testid="lightbox-next"
                >
                  <ChevronRight className="w-8 h-8 text-white" />
                </button>
              </>
            )}

            {selectedImage && (
              <div>
                <img
                  src={selectedImage.image}
                  alt={selectedImage.title}
                  className="w-full max-h-[80vh] object-contain"
                />
                <div className="p-6 bg-slate-900">
                  <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    {selectedImage.title}
                  </h3>
                  <p className="text-slate-400 capitalize">{selectedImage.project ? `${selectedImage.project} · ` : ''}{selectedImage.category}</p>
                  {selectedImage.description && (
                    <p className="text-slate-300 mt-2">{selectedImage.description}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* CTA Section */}
      <section className="section-padding bg-blue-600" data-testid="gallery-cta">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="heading-lg text-white mb-6">Like What You See?</h2>
            <p className="text-blue-100 text-lg mb-8">
              Let us bring this level of quality to your next project. Contact us today for a free consultation.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button size="lg" className="btn-white" data-testid="gallery-cta-quote-btn">
                  Get a Free Quote
                </Button>
              </Link>
              <a href="tel:+12363380659">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600" data-testid="gallery-cta-call-btn">
                  <Phone className="w-5 h-5 mr-2" />
                  (236) 338-0659
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
